import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { SectionHeading } from "./Section";
import { useLang } from "../../i18n";

const format = (n) => {
  if (n >= 1000) return (n / 1000).toFixed(n % 1000 === 0 ? 0 : 1) + "k";
  return String(n);
};

const Counter = ({ value, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const dur = 1600;
    const t0 = performance.now();
    const step = (now) => {
      const p = Math.min(1, (now - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.floor(eased * value));
      if (p < 1) requestAnimationFrame(step);
      else setDisplay(value);
    };
    requestAnimationFrame(step);
  }, [inView, value]);

  return (
    <span ref={ref} className="tabular-nums">
      {format(display)}{suffix}
    </span>
  );
};

export const Stats = () => {
  const { t } = useLang();
  const s = t.stats;

  return (
    <section className="relative py-24 md:py-36" data-testid="stats-section">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <SectionHeading overline={s.overline} title={s.title} />

        <div className="mt-16 grid grid-cols-2 lg:grid-cols-3 gap-px rounded-3xl overflow-hidden border border-white/10 bg-white/10">
          {s.items.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              className="group relative bg-ink p-8 md:p-12 transition-colors hover:bg-ink-surface"
              data-testid={`stat-${i}`}
            >
              <div className="font-heading text-5xl md:text-6xl lg:text-7xl font-black text-gradient-flame">
                <Counter value={item.v} suffix={item.suffix} />
              </div>
              <div className="mt-3 text-sm md:text-base text-white/50">{item.l}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
